import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Save, Plus, X, FolderTree, Tag } from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { ETabs } from "../../../components/ETabs";
import { Category, CategoryPayload } from "../../../types/category";

interface CategoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  category: Category | null;
  categories: Category[]; // Üst kategori seçimi için tüm liste
  onSave: (data: CategoryPayload) => void;
}

const initialData: CategoryPayload = {
  name: "",
  description: "",
  parentId: null,
};

export const CategoryDialog: React.FC<CategoryDialogProps> = ({
  isOpen,
  onClose,
  category,
  categories,
  onSave,
}) => {
  const [formData, setFormData] = useState<CategoryPayload>(initialData);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    if (category) {
      setFormData({
        name: category.name || "",
        description: category.description || "",
        parentId: category.parentId || null,
      });
    } else {
      setFormData(initialData);
    }
    setActiveIndex(0);
  }, [isOpen, category]);

  const handleChange = (field: keyof CategoryPayload, value: any) => {
    setFormData((p) => ({ ...p, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    onSave(formData);
  };

  // Üst kategori seçenekleri (Kategorinin kendisini parent seçmesini engelliyoruz)
  const parentOptions = useMemo(() => {
    return categories
      .filter((c) => c.id !== category?.id)
      .map((c) => ({ label: c.name, value: c.id }));
  }, [categories, category]);

  const tabs = useMemo(() => [
    {
      id: 'basic',
      header: <><Tag className="w-4 h-4 mr-2" /> Genel Bilgiler</>,
      content: (
        <div className="space-y-4 p-2">
          <div className="flex flex-col gap-2">
            <label>Kategori Adı *</label>
            <InputText
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Örn: Elektronik"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label>Üst Kategori</label>
            <Dropdown
              value={formData.parentId}
              options={parentOptions}
              onChange={(e) => handleChange('parentId', e.value)}
              placeholder="Ana Kategori (Opsiyonel)"
              showClear
              filter
            />
          </div>

          <div className="flex flex-col gap-2">
            <label>Açıklama</label>
            <InputTextarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              placeholder="Kategori açıklaması..."
            />
          </div>
        </div>
      )
    }
  ], [formData, parentOptions]);

  return (
    <Dialog
      header={category ? "Kategoriyi Düzenle" : "Yeni Kategori Ekle"}
      visible={isOpen}
      onHide={onClose}
      modal
      className="w-full lg:w-4/12 xl:w-3/12"
      footer={
        <div className="flex justify-end gap-3">
          <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
          <IconTextButton onClick={handleSubmit} icon={category ? Save : Plus} className="btn-new">
            {category ? "Güncelle" : "Kategori Ekle"}
          </IconTextButton>
        </div>
      }
    >
      <ETabs tabs={tabs} initialActiveIndex={activeIndex} onTabChange={setActiveIndex} />
    </Dialog>
  );
};